import React, { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Scale, Search, ArrowLeft, ShieldCheck, Mail } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { LEGAL_DOCS } from "../data/legalDocs";
import { SUPPORT_EMAIL } from "../lib/analytics";

const Section = ({ label, items }) => (
  <div className="mt-5">
    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-taven-gold mb-2">{label}</h4>
    <ul className="space-y-2">{items.map((t) => <li key={t.slice(0, 24)} className="text-sm text-muted-foreground leading-relaxed flex gap-2"><span className="text-taven-gold">•</span> {t}</li>)}</ul>
  </div>
);

export const LegalCenter = () => {
  const { slug } = useParams();
  const [q, setQ] = useState("");
  const active = useMemo(() => LEGAL_DOCS.find((x) => x.slug === slug), [slug]);
  const list = useMemo(() => LEGAL_DOCS.filter((x) => x.title.toLowerCase().includes(q.toLowerCase())), [q]);

  return (
    <div data-testid="legal-center-page">
      <PageHero eyebrow="Legal Documentation Center" title={active ? active.title : "TAVEN Legal & Compliance"} subtitle={active ? undefined : "Professional policy drafts covering customers, sellers, affiliates, students, certificates, privacy, and marketplace compliance for TAVEN SYSTEM LLC."} />
      <section className="px-4 md:px-8 lg:px-12 mx-auto max-w-7xl py-10 grid lg:grid-cols-[300px_1fr] gap-8">
        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="flex items-center gap-2 bg-white border border-black/15 px-3 h-11 mb-4">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input data-testid="legal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search policies..." className="bg-transparent outline-none text-sm w-full" />
          </div>
          <nav className="border border-black/10 bg-white divide-y divide-black/5 max-h-[65vh] overflow-y-auto">
            {list.map((x) => (
              <Link key={x.slug} to={`/legal/${x.slug}`} data-testid={`legal-link-${x.slug}`} className={`block px-4 py-3 text-sm hover:bg-taven-gray/50 ${active?.slug === x.slug ? "bg-taven-gray font-bold text-taven-gold" : ""}`}>{x.title}</Link>
            ))}
          </nav>
        </aside>

        <div className="bg-white border border-black/10 p-6 md:p-10 min-h-[50vh]">
          {active ? (
            <article data-testid="legal-content">
              <Link to="/legal" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-black mb-6"><ArrowLeft className="w-3.5 h-3.5" /> All Policies</Link>
              <h2 className="text-2xl md:text-3xl font-black font-display tracking-tight flex items-center gap-3"><Scale className="w-7 h-7 text-taven-gold" /> {active.title}</h2>
              <p className="text-sm text-muted-foreground mt-4"><strong>Purpose:</strong> {active.purpose}</p>
              <p className="text-sm text-muted-foreground mt-1"><strong>Who it applies to:</strong> {active.applies}</p>
              <Section label="Rules" items={active.rules} />
              <Section label="User Responsibilities" items={active.userResp} />
              <Section label="TAVEN Responsibilities" items={active.tavenResp} />
              <Section label="Limitations" items={active.limits} />
              <div className="mt-8 pt-5 border-t border-black/10 text-xs text-muted-foreground space-y-1">
                <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-taven-gold" /> Contact: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-taven-gold underline">{SUPPORT_EMAIL}</a></p>
                <p>Last updated: {active.updated}</p>
                <p className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-taven-gold" /> Professional draft — attorney review recommended before final publication.</p>
              </div>
            </article>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground mb-6">Browse the full TAVEN policy library. These are professional drafts; TAVEN recommends attorney review before final publication.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {LEGAL_DOCS.map((x) => (
                  <Link key={x.slug} to={`/legal/${x.slug}`} className="p-4 border border-black/5 hover:border-taven-gold hover-lift flex items-start gap-3 group">
                    <Scale className="w-5 h-5 text-taven-gold shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold group-hover:text-taven-gold">{x.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default LegalCenter;
